/**
 * Advanced text difference highlighter for markdown content
 * Highlights additions, deletions, and provides granular change tracking
 */

export interface Change {
  id: string;
  type: 'addition' | 'deletion' | 'modification';
  original: string;
  revised: string;
  startIndex: number;
  endIndex: number;
  explanation?: string;
}

interface DiffResult {
  original: string;
  revised: string;
  changes: Change[];
}

export const highlightDifferences = (original: string, revised: string): DiffResult => {
  // Split into sentences for better granularity
  const originalSentences = original.match(/[^.!?]+[.!?]+/g) || [original];
  const revisedSentences = revised.match(/[^.!?]+[.!?]+/g) || [revised];

  const changes: Change[] = [];
  const originalHighlighted: string[] = [];
  const revisedHighlighted: string[] = [];

  let changeIdCounter = 0;
  let i = 0, j = 0;
  let originalIndex = 0, revisedIndex = 0;

  while (i < originalSentences.length || j < revisedSentences.length) {
    if (i >= originalSentences.length) {
      // Only revised content left (additions)
      const changeId = `change-${changeIdCounter++}`;
      const sentence = revisedSentences[j];
      revisedHighlighted.push(`<mark class="bg-green-200 dark:bg-green-900 cursor-pointer hover:bg-green-300 dark:hover:bg-green-800 transition-colors" data-change-id="${changeId}">${escapeHtml(sentence)}</mark>`);
      changes.push({
        id: changeId,
        type: 'addition',
        original: '',
        revised: sentence,
        startIndex: revisedIndex,
        endIndex: revisedIndex + sentence.length,
        explanation: 'New content added'
      });
      revisedIndex += sentence.length;
      j++;
    } else if (j >= revisedSentences.length) {
      // Only original content left (deletions)
      const changeId = `change-${changeIdCounter++}`;
      const sentence = originalSentences[i];
      originalHighlighted.push(`<mark class="bg-red-200 dark:bg-red-900 line-through cursor-pointer hover:bg-red-300 dark:hover:bg-red-800 transition-colors" data-change-id="${changeId}">${escapeHtml(sentence)}</mark>`);
      changes.push({
        id: changeId,
        type: 'deletion',
        original: sentence,
        revised: '',
        startIndex: originalIndex,
        endIndex: originalIndex + sentence.length,
        explanation: 'Content removed'
      });
      originalIndex += sentence.length;
      i++;
    } else if (originalSentences[i] === revisedSentences[j]) {
      // Same sentence in both
      originalHighlighted.push(escapeHtml(originalSentences[i]));
      revisedHighlighted.push(escapeHtml(revisedSentences[j]));
      originalIndex += originalSentences[i].length;
      revisedIndex += revisedSentences[j].length;
      i++;
      j++;
    } else {
      // Different sentences - mark as modification
      const changeId = `change-${changeIdCounter++}`;
      const origSentence = originalSentences[i];
      const revSentence = revisedSentences[j];
      
      originalHighlighted.push(`<mark class="bg-yellow-200 dark:bg-yellow-900 cursor-pointer hover:bg-yellow-300 dark:hover:bg-yellow-800 transition-colors" data-change-id="${changeId}">${escapeHtml(origSentence)}</mark>`);
      revisedHighlighted.push(`<mark class="bg-blue-200 dark:bg-blue-900 cursor-pointer hover:bg-blue-300 dark:hover:bg-blue-800 transition-colors" data-change-id="${changeId}">${escapeHtml(revSentence)}</mark>`);
      
      changes.push({
        id: changeId,
        type: 'modification',
        original: origSentence,
        revised: revSentence,
        startIndex: originalIndex,
        endIndex: originalIndex + origSentence.length,
        explanation: 'Content modified for clarity or accuracy'
      });
      
      originalIndex += origSentence.length;
      revisedIndex += revSentence.length;
      i++;
      j++;
    }
  }

  return {
    original: originalHighlighted.join(''),
    revised: revisedHighlighted.join(''),
    changes,
  };
};

const escapeHtml = (text: string): string => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

// Apply individual changes to content
export const applyChange = (content: string, change: Change, accept: boolean): string => {
  if (accept) {
    if (change.type === 'addition') {
      return content + change.revised;
    } else if (change.type === 'deletion') {
      return content.replace(change.original, '');
    } else if (change.type === 'modification') {
      return content.replace(change.original, change.revised);
    }
  }
  return content;
};

// Batch apply multiple changes
export const applyChanges = (originalContent: string, changes: Change[], acceptedIds: Set<string>): string => {
  let result = originalContent;
  changes.forEach(change => {
    if (acceptedIds.has(change.id)) {
      result = applyChange(result, change, true);
    }
  });
  return result;
};

